import type {
  EditImageSaveResult,
  ImageEditCanvasContext,
  ImageEditPinIntent,
  ImageEditPinOutcome,
} from '@/types/edit-image';

export type CanvasEditedReferencePinHandler = (
  result: Extract<EditImageSaveResult, { kind: 'markup-reference' }>,
  sourceNodeId: string,
  intent?: ImageEditPinIntent
) => Promise<unknown>;

let registration: {
  handler: CanvasEditedReferencePinHandler;
  context?: ImageEditCanvasContext;
} | null = null;

export function registerCanvasEditedReferencePinHandler(
  handler: CanvasEditedReferencePinHandler | null,
  context?: ImageEditCanvasContext
): () => void {
  const entry = handler ? { handler, context } : null;
  registration = entry;
  if (handler && typeof window !== 'undefined') {
    window.dispatchEvent(new Event('jointo:image-edit-presentation-ready'));
  }
  return () => {
    if (registration === entry) registration = null;
  };
}

export function pinEditedReferenceOnOpenCanvas(
  result: Extract<EditImageSaveResult, { kind: 'markup-reference' }>,
  sourceNodeId: string,
  intent: ImageEditPinIntent
): Promise<ImageEditPinOutcome>;
export function pinEditedReferenceOnOpenCanvas(
  result: Extract<EditImageSaveResult, { kind: 'markup-reference' }>,
  sourceNodeId: string
): Promise<boolean>;
export async function pinEditedReferenceOnOpenCanvas(
  result: Extract<EditImageSaveResult, { kind: 'markup-reference' }>,
  sourceNodeId: string,
  intent?: ImageEditPinIntent
): Promise<boolean | ImageEditPinOutcome> {
  const entry = registration;
  if (!entry || !sourceNodeId) return intent ? { status: 'not_ready' } : false;
  if (intent) {
    const a = entry.context;
    const b = intent.canvas;
    const matches =
      a?.projectId === b.projectId &&
      a.contextType === b.contextType &&
      (a.contextType === 'storyboard' && b.contextType === 'storyboard'
        ? a.storyboardId === b.storyboardId
        : a.contextType === 'asset' &&
          b.contextType === 'asset' &&
          a.assetType === b.assetType &&
          a.assetId === b.assetId);
    if (!matches || !intent.isCurrent()) return { status: 'not_ready' };
  }
  const guardedIntent = intent
    ? {
        ...intent,
        isCurrent: () =>
          intent.isCurrent() &&
          JSON.stringify(registration?.context) === JSON.stringify(entry.context),
      }
    : undefined;
  const outcome = await entry.handler(result, sourceNodeId, guardedIntent);
  if (!intent) return Boolean(outcome);
  if (
    outcome &&
    typeof outcome === 'object' &&
    'status' in outcome &&
    ['placed', 'not_ready', 'cannot_place'].includes(String(outcome.status))
  ) {
    return outcome as ImageEditPinOutcome;
  }
  return { status: 'cannot_place', reason: '未能确认画布放置结果' };
}
